import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Upload, X } from 'lucide-react'

import type { PromptCategory, PromptItem } from '@/api/studio-prompt-library'
import { uploadPromptEffectMedia } from '@/api/studio-prompt-library'
import { ProtoDropdown, preventDismissOnProtoDropdown } from '@/features/studio/components/ui-kit'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'

export type PromptFormValues = {
  category_id: string
  name: string
  content: string
  remark: string
  effect_media_url: string
  effect_media_kind: '' | 'image' | 'video'
  sort_order: number
}

export function PromptFormModal({
  open,
  onClose,
  onSubmit,
  initial,
  categories,
  busy,
}: {
  open: boolean
  onClose: () => void
  onSubmit: (values: PromptFormValues) => Promise<void>
  initial?: PromptItem | null
  categories: PromptCategory[]
  busy?: boolean
}) {
  const { t } = useTranslation()
  const uploadRef = useRef<HTMLInputElement>(null)
  const [values, setValues] = useState<PromptFormValues>(emptyValues())
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setError('')
    if (initial) {
      setValues({
        category_id: initial.category_id ?? '',
        name: initial.name,
        content: initial.content,
        remark: initial.remark,
        effect_media_url: initial.effect_media_url,
        effect_media_kind: initial.effect_media_kind,
        sort_order: initial.sort_order,
      })
    } else {
      setValues(emptyValues())
    }
  }, [open, initial])

  const categoryOptions = [
    { value: '', label: t('promptLibrary.uncategorized') },
    ...categories.map((c) => ({ value: c.id, label: c.name })),
  ]

  const mediaKindOptions = [
    { value: '', label: t('promptLibrary.mediaNone') },
    { value: 'image', label: t('promptLibrary.mediaImage') },
    { value: 'video', label: t('promptLibrary.mediaVideo') },
  ]

  async function handleUpload(file: File) {
    const kind = values.effect_media_kind
    if (kind !== 'image' && kind !== 'video') return
    setUploading(true)
    setError('')
    try {
      const url = await uploadPromptEffectMedia(file, kind)
      setValues((v) => ({ ...v, effect_media_url: url }))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('promptLibrary.uploadFailed'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="max-w-lg gap-0 overflow-hidden p-0"
        showCloseButton={false}
        onPointerDownOutside={preventDismissOnProtoDropdown}
        onInteractOutside={preventDismissOnProtoDropdown}
        onFocusOutside={preventDismissOnProtoDropdown}
      >
        <DialogHeader className="flex-row items-center justify-between border-b border-white/[0.06] px-5 py-4">
          <DialogTitle className="text-base font-semibold text-zinc-50">
            {initial ? t('promptLibrary.editPrompt') : t('promptLibrary.createPrompt')}
          </DialogTitle>
          <Button type="button" variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('common.close')}>
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>
        <form
          className="studio-scrollbar-hidden max-h-[min(72vh,640px)] space-y-4 overflow-y-auto px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault()
            void onSubmit(values)
          }}
        >
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500">{t('promptLibrary.fieldCategory')}</Label>
            <ProtoDropdown
              value={values.category_id}
              options={categoryOptions}
              onValueChange={(category_id) => setValues((v) => ({ ...v, category_id }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500">{t('promptLibrary.fieldName')}</Label>
            <Input
              value={values.name}
              onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500">{t('promptLibrary.fieldContent')}</Label>
            <Textarea
              value={values.content}
              onChange={(e) => setValues((v) => ({ ...v, content: e.target.value }))}
              rows={6}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500">{t('promptLibrary.fieldRemark')}</Label>
            <Textarea
              value={values.remark}
              onChange={(e) => setValues((v) => ({ ...v, remark: e.target.value }))}
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-zinc-500">{t('promptLibrary.fieldEffectMedia')}</Label>
            <ProtoDropdown
              value={values.effect_media_kind}
              options={mediaKindOptions}
              onValueChange={(effect_media_kind) =>
                setValues((v) => ({
                  ...v,
                  effect_media_kind: effect_media_kind as PromptFormValues['effect_media_kind'],
                  effect_media_url: effect_media_kind ? v.effect_media_url : '',
                }))
              }
            />
            {values.effect_media_kind ? (
              <div className="flex items-center gap-3">
                {values.effect_media_url ? (
                  values.effect_media_kind === 'video' ? (
                    <video
                      src={resolveMediaDisplayUrl(values.effect_media_url)}
                      className="h-16 w-24 rounded-lg object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={resolveMediaDisplayUrl(values.effect_media_url)}
                      alt=""
                      className="h-16 w-24 rounded-lg object-cover"
                    />
                  )
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={uploading}
                  onClick={() => uploadRef.current?.click()}
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {t('promptLibrary.uploadEffect')}
                </Button>
                <input
                  ref={uploadRef}
                  type="file"
                  className="hidden"
                  accept={values.effect_media_kind === 'video' ? 'video/*' : 'image/*'}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void handleUpload(file)
                    e.currentTarget.value = ''
                  }}
                />
              </div>
            ) : null}
          </div>
          {error ? <p className="text-xs text-rose-400">{error}</p> : null}
          <DialogFooter className="gap-2 pt-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy || uploading}>
              {busy ? t('canvas.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function emptyValues(): PromptFormValues {
  return {
    category_id: '',
    name: '',
    content: '',
    remark: '',
    effect_media_url: '',
    effect_media_kind: '',
    sort_order: 0,
  }
}
